// MeetingContent.js

import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Timer from '../Timer/Timer';
import TranscriptionArea from '../TranscriptionArea/TranscriptionArea';
import MeetingEditor from './MeetingEditor';
import TopBar from '../TopBar/TopBar';
import { useRecording } from '../../hooks/useRecording';
import { useAudioStreaming } from '../../hooks/useAudioStreaming';
import { useAudioLevel } from '../../hooks/useAudioLevel';
import { useTranscription } from '../../hooks/useTranscription';
import { useRecordingError } from '../../hooks/useRecordingError';
import { useMicrophones } from '../../hooks/useMicrophones';
import { useSession } from '../../hooks/useSession';
import apiClient from '../../apiClient';
import streamClient from '../../streamClient';
import './MeetingContent.css';

const CHUNK_START_AFTER = 10;
const CHUNK_INTERVAL = 10;

function MeetingContent() {
  // URL query params
  const [searchParams] = useSearchParams();
  const meetingIdFromURL = searchParams.get('meeting_id') || null;
  const completedParam = searchParams.get('completed') === '1';

  // Hooks
  const recording = useRecording();
  const { transcription, transcriptionRef, isTranscribing, transcribeChunk, resetTranscription, setTranscriptionFromData } = useTranscription();
  const { error, handleError, clearError } = useRecordingError();
  const { microphones, selectedMicId, setSelectedMicId, captureSystemAudio, setCaptureSystemAudio } = useMicrophones(false);
  const { sessionId: meetingId, setSessionId: setMeetingId, createSession, updateSessionStatus, fetchSessionData } = useSession(meetingIdFromURL, completedParam, 'meeting');
  
  const { soundLevel, noAudioDetected } = useAudioLevel({
    isRecording: recording.isRecording,
    isPaused: recording.isPaused,
    selectedMicId,
  });

  const { transcriptionAreaRef } = useAudioStreaming({
    currentTime: recording.currentTime,
    isRecording: recording.isRecording,
    isPaused: recording.isPaused,
    chunkStartAfter: CHUNK_START_AFTER,
    chunkInterval: CHUNK_INTERVAL
  });

  // Visa MeetingEditor när mötet är avslutat
  const [showMeetingEditor, setShowMeetingEditor] = useState(false);

  // Mallhantering
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // =============================
  //     Hämta mötesmallar
  // =============================
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const resp = await apiClient.get('/api/meetingmallar');
        if (Array.isArray(resp.data)) {
          setTemplates(resp.data);
          if (resp.data.length > 0) {
            setSelectedTemplate(resp.data[0]);
          }
        } else {
          handleError('Fel vid hämtning av mötesmallar.');
        }
      } catch (err) {
        console.error('Kunde inte hämta mötesmallar:', err);
        handleError('Kunde inte hämta mötesmallar.');
      }
      setLoadingTemplates(false);
    };
    fetchTemplates();
  }, []);

  const handleChangeTemplate = (newTemplateId) => {
    const found = templates.find(
      (t) => t.meeting_template_id === parseInt(newTemplateId, 10)
    );
    if (found) {
      setSelectedTemplate(found);
    } else {
      handleError('Mall ej hittad.');
    }
  };

  // =============================
  //     Hämta existerande möte
  // =============================
  useEffect(() => {
    if (meetingIdFromURL) {
      handleFetchMeeting(meetingIdFromURL);
    }
  }, [meetingIdFromURL]);

  const handleFetchMeeting = async (id) => {
    try {
      const data = await fetchSessionData(id);
      if (data.transcription_text) {
        setTranscriptionFromData(data.transcription_text);
      }
      if (data.status === 'completed' || completedParam) {
        setShowMeetingEditor(true);
        recording.stop();
      } else if (data.status === 'paused') {
        recording.pause();
      } else if (data.status === 'in_progress') {
        recording.resume();
      }
    } catch (err) {
      handleError('Fel vid hämtning av möte', err);
    }
  };

  // Auto-pausa vid sidbyte
  useEffect(() => {
    return () => {
      if (recording.isRecording && !recording.isPaused) {
        pauseRecording();
      }
    };
  }, [recording.isRecording, recording.isPaused]);

  // =============================
  //      Inspelningskontroller
  // =============================
  const startMeeting = async () => {
    recording.start();
    clearError();
    resetTranscription();

    if (!meetingId) {
      try {
        const newMeetingId = await createSession(null, '/meetings/start');
        setMeetingId(newMeetingId);
      } catch (err) {
        handleError('Fel vid start av möte', err);
        recording.stop();
        return;
      }
    }

    if (transcriptionAreaRef.current) {
      transcriptionAreaRef.current.startRecording();
    }
  };

  const pauseRecording = async () => {
    if (!recording.isRecording || recording.isPaused) return;
    recording.pause();

    if (transcriptionAreaRef.current) {
      const blob = await transcriptionAreaRef.current.stopRecording();
      if (blob) {
        await transcribeChunk(blob, meetingId, '/meetings');
      }
    }

    if (meetingId) {
      try {
        await updateSessionStatus('pause');
      } catch (err) {
        handleError('Fel vid pauseMeeting', err);
      }
    }
  };

  const resumeRecording = async () => {
    if (!recording.isPaused) return;
    recording.resume();

    if (transcriptionAreaRef.current) {
      transcriptionAreaRef.current.startRecording();
    }

    if (meetingId) {
      try {
        await updateSessionStatus('resume');
      } catch (err) {
        handleError('Fel vid resumeMeeting', err);
      }
    }
  };

  const stopMeeting = async () => {
    recording.stop();

    if (transcriptionAreaRef.current) {
      const blob = await transcriptionAreaRef.current.stopRecording();
      if (blob) {
        await transcribeChunk(blob, meetingId, '/meetings');
      }
    }

    if (meetingId) {
      try {
        await updateSessionStatus('complete');
      } catch (err) {
        handleError('Fel vid completeMeeting', err);
      }
    }

    setShowMeetingEditor(true);
  };


  // =============================
  //   Visa MeetingEditor om mötet är avslutat
  // =============================
  if (showMeetingEditor) {
    return (
      <MeetingEditor
        meetingId={meetingId}
        rawTranscription={transcriptionRef.current}
      />
    );
  }

  // =============================
  //           Render
  // =============================
  return (
    <>
      <TopBar
        templates={templates}
        selectedTemplate={selectedTemplate}
        loadingTemplates={loadingTemplates}
        onTemplateChange={handleChangeTemplate}
        showTabs={false}
        showStopButton={recording.isRecording || recording.isPaused}
        onStopRecording={stopMeeting}
        startDateTime={recording.startDateTime}
        microphones={microphones}
        selectedMicId={selectedMicId}
        onChangeMic={(micId) => setSelectedMicId(micId)}
        captureSystemAudio={captureSystemAudio}
        onToggleSystemAudio={() => setCaptureSystemAudio((prev) => !prev)}
      />

      {/* Container för innehållet */}
      <div className="meeting-content-container">
        <div className="recording-wrapper">
          <h2 className="main-header">Mötesinspelning</h2>
          <Timer currentTime={recording.currentTime} />

          <div className="central-record-button-container">
            <button
              className={`central-record-button elongated ${
                recording.isRecording ? 'recording' : ''
              }`}
              style={{ '--sound-level': soundLevel }}
              onClick={() => {
                if (!recording.isRecording && !recording.isPaused) {
                  startMeeting();
                } else if (recording.isRecording && !recording.isPaused) {
                  pauseRecording();
                } else if (recording.isPaused) {
                  resumeRecording();
                }
              }}
            >
              {!recording.isRecording && !recording.isPaused
                ? 'Starta möte'
                : recording.isRecording && !recording.isPaused
                ? 'Pausa möte'
                : 'Återuppta möte'}
            </button>

            {recording.isRecording && !recording.isPaused && (
              <div
                className="record-background-ring"
                style={{ '--sound-level': soundLevel }}
              />
            )}

            {/* Med systemljud påslaget kan man tyst lyssna på motparten — mätningen
                ser bara mikrofonen, så varna inte då. */}
            {recording.isRecording && !recording.isPaused && noAudioDetected && !captureSystemAudio && (
              <div className="no-audio-warning" role="status">
                Inget ljud detekteras — talar du, och är rätt mikrofon vald?
              </div>
            )}
          </div>
          {/* "Avsluta möte"-knappen har tagits bort eftersom den nu hanteras i TopBar */}
        </div>

        {/* Dold TranscriptionArea */}
        <div style={{ display: 'none' }}>
          <TranscriptionArea
            ref={transcriptionAreaRef}
            onDataAvailable={(chunkBlob) => transcribeChunk(chunkBlob, meetingId, '/meetings')}
            selectedMicId={selectedMicId}
            captureSystemAudio={captureSystemAudio}
          />
        </div>

        {error && <p className="error-message">{error}</p>}
      </div>
    </>
  );
}

export default MeetingContent;
